import {
  useQueries,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  ExternalAssetListOrderByDto,
  ExternalNetworkEntityKind,
} from '@terra/shared-contracts-external-network';
import { terraProxyApiClient } from '../../clients/testerClient';
import {
  assetListFilterQueryKey,
  buildExternalNetworkAssetListQuery,
  kindSupportsAllAssetFilters,
  type ExternalNetworkAssetListFilters,
} from '../../components/tabs/surface-assets/build-asset-list-query';
import { EMPTY_ASSET_FILTER_OPTIONS } from '../../components/tabs/surface-assets/build-asset-filter-configs';
import { SURFACE_ASSETS_PAGE_SIZE } from '../../components/tabs/surface-assets/surfaceAsset.constants';
import {
  EXTERNAL_ASSETS_PAGE_SIZE,
  resolveNextAllKindsPageParam,
  type ExternalAssetsAllKindsPage,
  type ExternalAssetsAllKindsPageParam,
} from '../../components/tabs/surface-assets/external-assets-table/external-assets-table.model';

export const SURFACE_ASSETS_KEY = 'surface-assets';

const EMPTY_EXTERNAL_ASSETS: never[] = [];

export const EXTERNAL_NETWORK_ASSET_KINDS: ExternalNetworkEntityKind[] = [
  'DOMAIN',
  'HOST',
  'GATEWAY',
  'NETWORK_SERVICE',
  'NETBLOCK',
  'SOFTWARE',
  'STORAGE_SERVICE',
];

export async function fetchExternalNetworkAssets({
  surfaceId,
  entityKind,
  search,
  skip = 0,
  limit = SURFACE_ASSETS_PAGE_SIZE,
  filters,
  orderBy,
  groupBy,
}: {
  surfaceId: string;
  entityKind: ExternalNetworkEntityKind;
  search?: string;
  skip?: number;
  limit?: number;
  filters?: ExternalNetworkAssetListFilters;
  orderBy?: ExternalAssetListOrderByDto[];
  groupBy?: string;
}) {
  const queryArgs = {
    search,
    skip,
    limit,
    filters,
    orderBy,
    groupBy,
  };

  switch (entityKind) {
    case 'DOMAIN':
      return terraProxyApiClient.listSurfaceDomains({
        surfaceId,
        query: buildExternalNetworkAssetListQuery({
          entityKind: 'DOMAIN',
          ...queryArgs,
        }),
      });
    case 'HOST':
      return terraProxyApiClient.listSurfaceHosts({
        surfaceId,
        query: buildExternalNetworkAssetListQuery({
          entityKind: 'HOST',
          ...queryArgs,
        }),
      });
    case 'GATEWAY':
      return terraProxyApiClient.listSurfaceGateways({
        surfaceId,
        query: buildExternalNetworkAssetListQuery({
          entityKind: 'GATEWAY',
          ...queryArgs,
        }),
      });
    case 'NETWORK_SERVICE':
      return terraProxyApiClient.listSurfaceServices({
        surfaceId,
        query: buildExternalNetworkAssetListQuery({
          entityKind: 'NETWORK_SERVICE',
          ...queryArgs,
        }),
      });
    case 'NETBLOCK':
      return terraProxyApiClient.listSurfaceNetblocks({
        surfaceId,
        query: buildExternalNetworkAssetListQuery({
          entityKind: 'NETBLOCK',
          ...queryArgs,
        }),
      });
    case 'SOFTWARE':
      return terraProxyApiClient.listSurfaceSoftware({
        surfaceId,
        query: buildExternalNetworkAssetListQuery({
          entityKind: 'SOFTWARE',
          ...queryArgs,
        }),
      });
    case 'STORAGE_SERVICE':
      return terraProxyApiClient.listSurfaceStorageServices({
        surfaceId,
        query: buildExternalNetworkAssetListQuery({
          entityKind: 'STORAGE_SERVICE',
          ...queryArgs,
        }),
      });
  }
}

type UseExternalNetworkAssetsParams = {
  surfaceId: string | null;
  entityKind: ExternalNetworkEntityKind;
  search?: string;
  skip?: number;
  enabled?: boolean;
  filters?: ExternalNetworkAssetListFilters;
};

export function useExternalNetworkAssets({
  surfaceId,
  entityKind,
  search,
  skip = 0,
  enabled = true,
  filters,
}: UseExternalNetworkAssetsParams) {
  const query = useQuery({
    queryKey: [
      SURFACE_ASSETS_KEY,
      'external',
      surfaceId,
      entityKind,
      search,
      skip,
      ...(filters ? assetListFilterQueryKey(filters) : []),
    ],
    queryFn: () =>
      fetchExternalNetworkAssets({
        surfaceId: surfaceId ?? '',
        entityKind,
        search,
        skip,
        filters,
      }),
    enabled: Boolean(surfaceId) && enabled,
  });

  return {
    data: query.data?.data ?? EMPTY_EXTERNAL_ASSETS,
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export async function fetchExternalNetworkAllKindsPage({
  surfaceId,
  search,
  filters,
  orderBy,
  pageParam,
}: {
  surfaceId: string;
  search?: string;
  filters: ExternalNetworkAssetListFilters;
  orderBy?: ExternalAssetListOrderByDto[];
  pageParam: ExternalAssetsAllKindsPageParam;
}): Promise<ExternalAssetsAllKindsPage> {
  const page = await fetchExternalNetworkAssets({
    surfaceId,
    entityKind: pageParam.entityKind,
    search,
    skip: pageParam.skip,
    limit: EXTERNAL_ASSETS_PAGE_SIZE,
    filters,
    orderBy,
  });

  const nextKindParam =
    page.data.length === 0
      ? resolveNextAllKindsPageParam({
          kinds: EXTERNAL_NETWORK_ASSET_KINDS,
          supportsKind: (entityKind) =>
            kindSupportsAllAssetFilters({ entityKind, filters }),
          lastPage: { ...pageParam, rowCount: 0, total: page.total },
        })
      : undefined;

  if (nextKindParam) {
    return fetchExternalNetworkAllKindsPage({
      surfaceId,
      search,
      filters,
      orderBy,
      pageParam: nextKindParam,
    });
  }

  return { ...pageParam, data: page.data, total: page.total };
}

const combineExternalNetworkKindTotals = (
  results: UseQueryResult<number>[],
) => {
  const totals = new Map<string, number>(
    EXTERNAL_NETWORK_ASSET_KINDS.map((entityKind, index) => [
      entityKind,
      results[index]?.data ?? 0,
    ]),
  );

  return {
    totals,
    total: [...totals.values()].reduce((sum, count) => sum + count, 0),
    isLoading: results.some((result) => result.isLoading),
    isError: results.some((result) => result.isError),
  };
};

export function useExternalNetworkKindTotals({
  surfaceId,
  search,
  filters,
  enabled = true,
}: {
  surfaceId: string | null;
  search?: string;
  filters?: ExternalNetworkAssetListFilters;
  enabled?: boolean;
}) {
  const filterKey = filters ? assetListFilterQueryKey(filters) : [];

  return useQueries({
    queries: EXTERNAL_NETWORK_ASSET_KINDS.map((entityKind) => ({
      queryKey: [
        SURFACE_ASSETS_KEY,
        'external-kind-total',
        surfaceId,
        entityKind,
        search,
        ...filterKey,
      ],
      queryFn: async (): Promise<number> => {
        const page = await fetchExternalNetworkAssets({
          surfaceId: surfaceId ?? '',
          entityKind,
          search,
          filters,
          limit: 1,
        });

        return page.total;
      },
      enabled:
        Boolean(surfaceId) &&
        enabled &&
        kindSupportsAllAssetFilters({ entityKind, filters }),
    })),
    combine: combineExternalNetworkKindTotals,
  });
}

export function useExternalNetworkAssetFilterOptions(surfaceId: string | null) {
  const query = useQuery({
    queryKey: [SURFACE_ASSETS_KEY, 'filter-options', surfaceId],
    queryFn: () =>
      terraProxyApiClient.getSurfaceAssetFilterOptions(surfaceId ?? ''),
    enabled: Boolean(surfaceId),
  });

  return {
    options: query.data ?? EMPTY_ASSET_FILTER_OPTIONS,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
